import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "react-query";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import LoadingButton from "../components/LoadingButton";
import {
  getClinicianOnboarding,
  patchClinicianOnboarding,
  setClinicianOnboardingPassword,
} from "../api/services/user";
import PasswordField from "../components/PasswordField";
import { logo } from "../assets/index";
import {
  CLINICIAN_APP_STORE_URL,
  CLINICIAN_PLAY_STORE_URL,
  ONBOARDING_DAYS,
} from "../constants/onboarding";

const STEPS = [
  "Welcome",
  "Personal",
  "Professional",
  "Availability",
  "Payout",
  "Password",
  "Done",
];

type FormState = {
  fullName: string;
  email: string;
  mobileNumber: string;
  gender: string;
  dob: string;
  age: string;
  location: string;
  bio: string;
  languagesKnown: string;
  designation: string;
  experience: string;
  license: string;
  licenseExpiration: string;
  serviceIds: number[];
  enabledDays: string[];
  bankName: string;
  bankCode: string;
  bankAccountNumber: string;
  password: string;
  confirmPassword: string;
};

const emptyForm: FormState = {
  fullName: "",
  email: "",
  mobileNumber: "",
  gender: "",
  dob: "",
  age: "",
  location: "",
  bio: "",
  languagesKnown: "",
  designation: "",
  experience: "",
  license: "",
  licenseExpiration: "",
  serviceIds: [],
  enabledDays: [],
  bankName: "",
  bankCode: "",
  bankAccountNumber: "",
  password: "",
  confirmPassword: "",
};

function calcAge(dob: string): string {
  if (!dob) return "";
  const birth = new Date(dob);
  if (Number.isNaN(birth.getTime())) return "";
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age -= 1;
  return age > 0 ? String(age) : "";
}

const Onboard = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const [activeStep, setActiveStep] = useState(0);
  const [form, setForm] = useState<FormState>({ ...emptyForm });
  const [completed, setCompleted] = useState(false);

  const {
    data,
    isLoading,
    isError,
    error,
  } = useQuery(
    ["clinician-onboarding", token],
    () => getClinicianOnboarding(token),
    {
      enabled: !!token,
      retry: false,
      refetchOnWindowFocus: false,
    }
  );

  const onboardData = data?.data?.data || data?.data;
  const availableServices = onboardData?.availableServices || [];

  useEffect(() => {
    if (!onboardData) return;
    const u = onboardData.user || {};
    const c = onboardData.clinician || {};
    setForm((prev) => ({
      ...prev,
      fullName: u.fullName || "",
      email: u.email || "",
      mobileNumber: u.mobileNumber || "",
      gender: c.gender && c.gender !== "OTHERS" ? c.gender : "",
      dob: c.dob || "",
      age: c.age != null && c.age !== 0 ? String(c.age) : "",
      location: c.location || "",
      bio: c.bio || "",
      languagesKnown: c.languagesKnown || "",
      designation: c.designation || "",
      experience:
        c.experience != null && c.experience !== 0 ? String(c.experience) : "",
      license: c.license || "",
      licenseExpiration: c.licenseExpiration || "",
      serviceIds: c.serviceIds || [],
      enabledDays: c.enabledDays || [],
      bankName: c.bankName || "",
      bankCode: c.bankCode || "",
      bankAccountNumber: c.bankAccountNumber || "",
    }));
  }, [onboardData]);

  const patchMutation = useMutation(
    (payload: any) => patchClinicianOnboarding(token, payload),
    {
      onError: (err: any) => {
        toast.error(
          err?.response?.data?.message ||
            "Could not save your progress. Please try again."
        );
      },
    }
  );

  const passwordMutation = useMutation(
    (password: string) => setClinicianOnboardingPassword(token, password),
    {
      onSuccess: () => {
        setCompleted(true);
        setActiveStep(STEPS.length - 1);
        toast.success("Your account is ready. Download the app to sign in.");
      },
      onError: (err: any) => {
        toast.error(
          err?.response?.data?.message ||
            "Could not set password. Please try again."
        );
      },
    }
  );

  const tokenErrorMessage = useMemo(() => {
    if (!token) return "This invitation link is missing a token.";
    if (!isError) return "";
    return (
      (error as any)?.response?.data?.message ||
      "This invitation link is invalid or has expired."
    );
  }, [token, isError, error]);

  const updateField = (field: keyof FormState) => (e: any) => {
    const value = e?.target?.value ?? e;
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      if (field === "dob") next.age = calcAge(value);
      return next;
    });
  };

  const toggleService = (id: number) => {
    setForm((prev) => ({
      ...prev,
      serviceIds: prev.serviceIds.includes(id)
        ? prev.serviceIds.filter((s) => s !== id)
        : [...prev.serviceIds, id],
    }));
  };

  const toggleDay = (day: string) => {
    setForm((prev) => ({
      ...prev,
      enabledDays: prev.enabledDays.includes(day)
        ? prev.enabledDays.filter((d) => d !== day)
        : [...prev.enabledDays, day],
    }));
  };

  const buildPersonalPayload = () => {
    const payload: any = {};
    if (form.fullName.trim()) payload.fullName = form.fullName.trim();
    if (form.mobileNumber.trim())
      payload.mobileNumber = form.mobileNumber.trim();
    if (form.gender) payload.gender = form.gender;
    if (form.dob) {
      payload.dob = form.dob;
      if (form.age) payload.age = Number(form.age);
    }
    if (form.location.trim()) payload.location = form.location.trim();
    if (form.bio.trim()) payload.bio = form.bio.trim();
    if (form.languagesKnown.trim())
      payload.languagesKnown = form.languagesKnown.trim();
    return payload;
  };

  const buildProfessionalPayload = () => {
    const payload: any = {};
    if (form.designation.trim()) payload.designation = form.designation.trim();
    if (form.experience !== "") payload.experience = Number(form.experience);
    if (form.license.trim()) payload.license = form.license.trim();
    if (form.licenseExpiration)
      payload.licenseExpiration = form.licenseExpiration;
    payload.serviceIds = form.serviceIds;
    return payload;
  };

  const buildBankPayload = () => {
    const payload: any = {};
    if (form.bankName.trim()) payload.bankName = form.bankName.trim();
    if (form.bankCode.trim()) payload.bankCode = form.bankCode.trim();
    if (form.bankAccountNumber.trim())
      payload.bankAccountNumber = form.bankAccountNumber.trim();
    return payload;
  };

  const saveAndNext = async (payload: any, nextStep: number) => {
    if (Object.keys(payload).length > 0) {
      await patchMutation.mutateAsync(payload);
    }
    setActiveStep(nextStep);
  };

  const handleNext = async () => {
    try {
      if (activeStep === 0) {
        setActiveStep(1);
        return;
      }
      if (activeStep === 1) {
        await saveAndNext(buildPersonalPayload(), 2);
        return;
      }
      if (activeStep === 2) {
        await saveAndNext(buildProfessionalPayload(), 3);
        return;
      }
      if (activeStep === 3) {
        await saveAndNext({ enabledDays: form.enabledDays }, 4);
        return;
      }
      if (activeStep === 4) {
        await saveAndNext(buildBankPayload(), 5);
        return;
      }
      if (activeStep === 5) {
        if (!form.password || form.password.length < 8) {
          toast.error("Password must be at least 8 characters");
          return;
        }
        if (form.password !== form.confirmPassword) {
          toast.error("Passwords do not match");
          return;
        }
        await passwordMutation.mutateAsync(form.password);
      }
    } catch {
      // errors toasted in mutations
    }
  };

  const handleSkip = async () => {
    if (activeStep === 3 || activeStep === 4) {
      setActiveStep(activeStep + 1);
    }
  };

  const saving = patchMutation.isLoading || passwordMutation.isLoading;

  if (!token || isError) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          bgcolor: "#f2f5f9",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 3,
        }}
      >
        <Paper sx={{ maxWidth: 520, p: 4, textAlign: "center" }}>
          <img src={logo} alt="Talaqah" style={{ height: 48, marginBottom: 16 }} />
          <Typography variant="h5" sx={{ mb: 1, fontFamily: "Inter_semibold" }}>
            Invitation unavailable
          </Typography>
          <Alert severity="error" sx={{ textAlign: "left", mb: 2 }}>
            {tokenErrorMessage}
          </Alert>
          <Typography color="text.secondary">
            Please contact your Talaqah administrator to request a new invite.
          </Typography>
        </Paper>
      </Box>
    );
  }

  if (isLoading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "#f2f5f9",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f2f5f9", py: { xs: 3, md: 5 }, px: 2 }}>
      <Box sx={{ maxWidth: 860, mx: "auto" }}>
        <Box sx={{ textAlign: "center", mb: 3 }}>
          <img src={logo} alt="Talaqah" style={{ height: 52 }} />
          <Typography
            variant="h4"
            sx={{ mt: 2, fontFamily: "Inter_semibold", color: "primary.main" }}
          >
            Clinician onboarding
          </Typography>
          <Typography color="text.secondary">
            Complete your profile and create a password to join Talaqah.
          </Typography>
        </Box>

        <Paper sx={{ p: { xs: 2.5, md: 4 }, borderRadius: 3 }}>
          <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
            {STEPS.map((label) => (
              <Step key={label} completed={completed && label === "Done"}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {activeStep === 0 && (
            <Box>
              <Typography variant="h5" sx={{ mb: 1, fontFamily: "Inter_semibold" }}>
                Welcome, {form.fullName || "clinician"}
              </Typography>
              <Typography color="text.secondary" sx={{ mb: 2 }}>
                Your account has been created with{" "}
                <strong>{form.email}</strong>. Most profile fields are optional —
                you can fill them in now or later in the app. You will create your
                password at the end.
              </Typography>
              <Alert severity="info" sx={{ mb: 2 }}>
                Completing your professional profile helps patients find the right
                clinician for their needs.
              </Alert>
            </Box>
          )}

          {activeStep === 1 && (
            <Box>
              <Typography variant="h6" sx={{ mb: 2, fontFamily: "Inter_semibold" }}>
                Personal details
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Full name"
                    value={form.fullName}
                    onChange={updateField("fullName")}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Email"
                    value={form.email}
                    disabled
                    helperText="Provided by your administrator"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Mobile number"
                    value={form.mobileNumber}
                    onChange={updateField("mobileNumber")}
                    placeholder="+9665XXXXXXXX"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Gender</InputLabel>
                    <Select
                      label="Gender"
                      value={form.gender}
                      onChange={updateField("gender")}
                    >
                      <MenuItem value="">Prefer not to say</MenuItem>
                      <MenuItem value="MALE">Male</MenuItem>
                      <MenuItem value="FEMALE">Female</MenuItem>
                      <MenuItem value="OTHERS">Other</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    type="date"
                    label="Date of birth"
                    InputLabelProps={{ shrink: true }}
                    value={form.dob}
                    onChange={updateField("dob")}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Age"
                    value={form.age}
                    onChange={updateField("age")}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Location"
                    value={form.location}
                    onChange={updateField("location")}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Languages known"
                    value={form.languagesKnown}
                    onChange={updateField("languagesKnown")}
                    placeholder="English, Arabic"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    minRows={3}
                    label="Short bio"
                    value={form.bio}
                    onChange={updateField("bio")}
                  />
                </Grid>
              </Grid>
            </Box>
          )}

          {activeStep === 2 && (
            <Box>
              <Typography variant="h6" sx={{ mb: 2, fontFamily: "Inter_semibold" }}>
                Professional details
              </Typography>
              <Typography color="text.secondary" sx={{ mb: 2 }}>
                Optional — a complete profile builds trust with patients and the
                care team.
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Designation"
                    value={form.designation}
                    onChange={updateField("designation")}
                    placeholder="Speech Therapist"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Years of experience"
                    type="number"
                    value={form.experience}
                    onChange={updateField("experience")}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="License number"
                    value={form.license}
                    onChange={updateField("license")}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    type="date"
                    label="License expiration"
                    InputLabelProps={{ shrink: true }}
                    value={form.licenseExpiration}
                    onChange={updateField("licenseExpiration")}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Services
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                    {availableServices.map((svc: any) => {
                      const selected = form.serviceIds.includes(svc.id);
                      return (
                        <Chip
                          key={svc.id}
                          label={svc.title}
                          color={selected ? "primary" : "default"}
                          variant={selected ? "filled" : "outlined"}
                          onClick={() => toggleService(svc.id)}
                        />
                      );
                    })}
                    {!availableServices.length && (
                      <Typography color="text.secondary">
                        No services available to select yet.
                      </Typography>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </Box>
          )}

          {activeStep === 3 && (
            <Box>
              <Typography variant="h6" sx={{ mb: 1, fontFamily: "Inter_semibold" }}>
                Availability
              </Typography>
              <Typography color="text.secondary" sx={{ mb: 2 }}>
                Select the days you typically work. You can refine exact time slots
                later in the clinician app. This step is optional.
              </Typography>
              <FormGroup row sx={{ gap: 1 }}>
                {ONBOARDING_DAYS.map((day) => (
                  <FormControlLabel
                    key={day}
                    control={
                      <Checkbox
                        checked={form.enabledDays.includes(day)}
                        onChange={() => toggleDay(day)}
                      />
                    }
                    label={day}
                  />
                ))}
              </FormGroup>
            </Box>
          )}

          {activeStep === 4 && (
            <Box>
              <Typography variant="h6" sx={{ mb: 1, fontFamily: "Inter_semibold" }}>
                Bank / payout details
              </Typography>
              <Typography color="text.secondary" sx={{ mb: 2 }}>
                Optional — needed for payouts. You can add these later from your
                profile.
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Bank name"
                    value={form.bankName}
                    onChange={updateField("bankName")}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Bank code / IBAN routing"
                    value={form.bankCode}
                    onChange={updateField("bankCode")}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Account number"
                    value={form.bankAccountNumber}
                    onChange={updateField("bankAccountNumber")}
                  />
                </Grid>
              </Grid>
            </Box>
          )}

          {activeStep === 5 && (
            <Box>
              <Typography variant="h6" sx={{ mb: 1, fontFamily: "Inter_semibold" }}>
                Create your password
              </Typography>
              <Typography color="text.secondary" sx={{ mb: 2 }}>
                Use this password with <strong>{form.email}</strong> to sign in to
                the Talaqah clinician app.
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <PasswordField
                    label="Password"
                    value={form.password}
                    onChange={updateField("password")}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <PasswordField
                    label="Confirm password"
                    name="confirmPassword"
                    value={form.confirmPassword}
                    onChange={updateField("confirmPassword")}
                    required
                  />
                </Grid>
              </Grid>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                Minimum 8 characters.
              </Typography>
            </Box>
          )}

          {activeStep === 6 && (
            <Box sx={{ textAlign: "center" }}>
              <Typography variant="h5" sx={{ mb: 1, fontFamily: "Inter_semibold" }}>
                You&apos;re all set
              </Typography>
              <Typography color="text.secondary" sx={{ mb: 3 }}>
                Download the Talaqah clinician app and sign in with{" "}
                <strong>{form.email}</strong> and the password you just created.
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  justifyContent: "center",
                  flexWrap: "wrap",
                }}
              >
                <Button
                  variant="contained"
                  href={CLINICIAN_APP_STORE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Download on the App Store
                </Button>
                <Button
                  variant="outlined"
                  href={CLINICIAN_PLAY_STORE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Get it on Google Play
                </Button>
              </Box>
            </Box>
          )}

          {activeStep < 6 && (
            <Box
              sx={{
                mt: 4,
                display: "flex",
                justifyContent: "space-between",
                gap: 2,
                flexWrap: "wrap",
              }}
            >
              <Button
                disabled={activeStep === 0 || saving}
                onClick={() => setActiveStep((s) => Math.max(0, s - 1))}
              >
                Back
              </Button>
              <Box sx={{ display: "flex", gap: 1 }}>
                {(activeStep === 3 || activeStep === 4) && (
                  <Button disabled={saving} onClick={handleSkip}>
                    Skip for now
                  </Button>
                )}
                <LoadingButton
                  variant="contained"
                  loading={saving}
                  onClick={handleNext}
                  title={
                    activeStep === 5
                      ? "Set password & finish"
                      : activeStep === 0
                      ? "Get started"
                      : "Continue"
                  }
                />
              </Box>
            </Box>
          )}
        </Paper>
      </Box>
    </Box>
  );
};

export default Onboard;
